<script setup lang="ts">
import type { MemberType } from '~/composables/useMonitors'
import { logApiError } from '~/utils/logger'

const teamPerson1 = '/images/logo.png'

interface Monitor {
  id: string
  name: string
  role: string
  job: string | null
  diploma: string | null
  bio: string | null
  image_url: string | null
  display_order: number
  member_type: MemberType
}

// Composable
const { getPublicMonitors } = useMonitors()

// State
const boardMembers = ref<Monitor[]>([])
const monitors = ref<Monitor[]>([])
const loading = ref(false)

// Background colors for cards (cycles through these)
const cardColors = [
  { backgroundColor: 'rgba(105, 108, 255, 0.16)', borderColor: 'rgba(105, 108, 255, 0.16)' },
  { backgroundColor: 'rgba(3, 195, 236, 0.16)', borderColor: 'rgba(3, 195, 236, 0.16)' },
  { backgroundColor: 'rgba(255, 62, 29, 0.16)', borderColor: 'rgba(255, 62, 29, 0.16)' },
  { backgroundColor: 'rgba(113, 221, 55, 0.16)', borderColor: 'rgba(113, 221, 55, 0.16)' },
]

// Fetch active team members ordered by display_order (public endpoint - no auth required)
async function fetchTeamMembers() {
  loading.value = true
  try {
    // Fetch board members and monitors separately
    const [boardResponse, monitorsResponse] = await Promise.all([
      getPublicMonitors(100, 'board'),
      getPublicMonitors(100, 'monitor'),
    ])

    boardMembers.value = boardResponse.data
    monitors.value = monitorsResponse.data
  }
  catch (error) {
    logApiError('/api/monitors/public', error, { operation: 'fetchTeamMembers' })
  }
  finally {
    loading.value = false
  }
}

// Get color for card based on index
function getCardColor(index: number) {
  return cardColors[index % cardColors.length]
}

// Lifecycle
onMounted(() => {
  fetchTeamMembers()
})
</script>

<template>
  <VContainer id="monitoren">
    <div class="our-team">
      <!-- Loading State -->
      <div
        v-if="loading"
        class="d-flex justify-center py-10"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </div>

      <template v-else>
        <!-- Board Members Section -->
        <div
          v-if="boardMembers.length"
          class="team-section"
        >
          <div class="headers d-flex justify-center flex-column align-center">
            <VChip
              label
              color="warning"
              size="x-large"
              class="ma-2"
              style="font-size: 1rem;font-weight: 600;text-transform: uppercase;"
            >
              Ons bestuur
            </VChip>

            <h4 class="d-flex align-center text-h4 mb-1 flex-wrap justify-center">
              maak kennis met het team dat alles in beweging zet
            </h4>
          </div>

          <VRow>
            <VCol
              v-for="(member, index) in boardMembers"
              :key="member.id"
              cols="12"
              lg="6"
              sm="12"
            >
              <VCard
                flat
                class="position-relative overflow-visible team-card mb-8"
              >
                <VRow
                  no-gutters
                  class="team-card-row"
                >
                  <VCol
                    cols="12"
                    md="5"
                  >
                    <div
                      class="team-image-wrapper position-relative"
                      :style="{
                        maxHeight: '800px',
                        minHeight: '280px',
                        borderRadius: '90px 20px 0 0',
                        backgroundColor: getCardColor(index)!.backgroundColor,
                        border: `1px solid ${getCardColor(index)!.borderColor}`,
                        borderBottom: 'none',
                      }"
                    >
                      <VImg
                        :src="member.image_url || teamPerson1"
                        height="520"
                        class="team-image"
                      />
                    </div>
                  </VCol>
                  <VCol
                    cols="12"
                    md="7"
                  >
                    <VCardText class="pa-6">
                      <h5 class="text-h5 mb-1">
                        {{ member.name }}
                      </h5>
                      <VChip
                        label
                        size="small"
                        color="warning"
                        variant="tonal"
                        class="mb-4"
                      >
                        {{ member.role }}
                      </VChip>

                      <div
                        v-if="member.job || member.diploma"
                        class="monitor-info-grid"
                      >
                        <div
                          v-if="member.job"
                          class="info-item"
                        >
                          <div class="info-icon">
                            <VIcon
                              icon="bx-briefcase"
                              size="20"
                              color="primary"
                            />
                          </div>
                          <div class="info-content">
                            <span class="info-label">Werk</span>
                            <span class="info-value">{{ member.job }}</span>
                          </div>
                        </div>

                        <div
                          v-if="member.diploma"
                          class="info-item"
                        >
                          <div class="info-icon">
                            <VIcon
                              icon="bx-bxs-graduation"
                              size="20"
                              color="success"
                            />
                          </div>
                          <div class="info-content">
                            <span class="info-label">Diploma</span>
                            <span class="info-value">{{ member.diploma }}</span>
                          </div>
                        </div>
                      </div>

                      <p
                        v-if="member.bio"
                        class="text-body-2 mt-4 mb-0 monitor-bio"
                      >
                        {{ member.bio }}
                      </p>
                    </VCardText>
                  </VCol>
                </VRow>
              </VCard>
            </VCol>
          </VRow>
        </div>

        <!-- Monitors Section -->
        <div
          v-if="monitors.length"
          class="team-section"
        >
          <div class="headers d-flex justify-center flex-column align-center">
            <VChip
              label
              color="primary"
              size="x-large"
              class="ma-2"
              style="font-size: 1rem;font-weight: 600;text-transform: uppercase;"
            >
              Onze monitoren
            </VChip>

            <h4 class="d-flex align-center text-h4 mb-1 flex-wrap justify-center">
              ontmoet de mensen die van elke dag een beleving maken
            </h4>
          </div>

          <VRow>
            <VCol
              v-for="(monitor, index) in monitors"
              :key="monitor.id"
              cols="12"
              lg="6"
              sm="12"
            >
              <VCard
                flat
                class="position-relative overflow-visible team-card mb-8"
              >
                <VRow
                  no-gutters
                  class="team-card-row"
                >
                  <VCol
                    cols="12"
                    md="5"
                  >
                    <div
                      class="team-image-wrapper position-relative"
                      :style="{
                        maxHeight: '800px',
                        minHeight: '280px',
                        borderRadius: '90px 20px 0 0',
                        backgroundColor: getCardColor(index)!.backgroundColor,
                        border: `1px solid ${getCardColor(index)!.borderColor}`,
                        borderBottom: 'none',
                      }"
                    >
                      <VImg
                        :src="monitor.image_url || teamPerson1"
                        height="520"
                        class="team-image"
                      />
                    </div>
                  </VCol>
                  <VCol
                    cols="12"
                    md="7"
                  >
                    <VCardText class="pa-6">
                      <h5 class="text-h5 mb-1">
                        {{ monitor.name }}
                      </h5>
                      <VChip
                        label
                        size="small"
                        color="primary"
                        variant="tonal"
                        class="mb-4"
                      >
                        {{ monitor.role }}
                      </VChip>

                      <div
                        v-if="monitor.job || monitor.diploma"
                        class="monitor-info-grid"
                      >
                        <div
                          v-if="monitor.job"
                          class="info-item"
                        >
                          <div class="info-icon">
                            <VIcon
                              icon="bx-briefcase"
                              size="20"
                              color="primary"
                            />
                          </div>
                          <div class="info-content">
                            <span class="info-label">Werk</span>
                            <span class="info-value">{{ monitor.job }}</span>
                          </div>
                        </div>

                        <div
                          v-if="monitor.diploma"
                          class="info-item"
                        >
                          <div class="info-icon">
                            <VIcon
                              icon="bx-bxs-graduation"
                              size="20"
                              color="success"
                            />
                          </div>
                          <div class="info-content">
                            <span class="info-label">Diploma</span>
                            <span class="info-value">{{ monitor.diploma }}</span>
                          </div>
                        </div>
                      </div>

                      <p
                        v-if="monitor.bio"
                        class="text-body-2 mt-4 mb-0 monitor-bio"
                      >
                        {{ monitor.bio }}
                      </p>
                    </VCardText>
                  </VCol>
                </VRow>
              </VCard>
            </VCol>
          </VRow>
        </div>

        <!-- Empty State -->
        <div
          v-if="!boardMembers.length && !monitors.length"
          class="text-center py-10"
        >
          <p class="text-body-1 text-disabled">
            Geen teamleden gevonden
          </p>
        </div>
      </template>
    </div>
  </VContainer>
</template>

<style lang="scss" scoped>
.headers {
  margin-block-end: 3rem;
}

.our-team {
  margin-block: 5.25rem;
}

.team-section {
  margin-block-end: 5rem;
}

@media (max-width: 1264px) {
  .our-team {
    margin-block-end: 1rem;
  }
}

.team-card-row {
  overflow: visible;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 90px 20px 12px 12px;
}

.team-card {
  overflow: visible;
}

.team-image-wrapper {
  inline-size: 100%;
}

.team-image {
  position: absolute;
  inset-inline: 0;
}

.section-title {
  font-size: 24px;
  font-weight: 800;
  line-height: 36px;
}

.section-title::after {
  position: absolute;

  /* background: url("@images/front-pages/icons/section-title-icon.png") no-repeat left bottom; */
  background-size: contain;
  block-size: 100%;
  content: "";
  font-weight: 800;
  inline-size: 120%;
  inset-block-end: 12%;
  inset-inline-start: -12%;
}

// Monitor info styling
.monitor-info-grid {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-item {
  display: flex;
  align-items: flex-start;
  border-radius: 8px;
  gap: 2px;
  padding-block: 10px;
  padding-inline: 14px;
}

.info-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-inline-size: 0;
}

.info-label {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.info-value {
  color: rgba(var(--v-theme-on-surface), 0.87);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.monitor-bio {
  border-block-start: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  color: rgba(var(--v-theme-on-surface), 0.7);
  line-height: 1.6;
  padding-block-start: 8px;
}
</style>
